import { useEffect, useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { createPurchaseOrderStockReceipt } from '#/api/srm/purchaseOrder/createStockReceipt';
import { fetchPurchaseOrderDetail } from '#/api/srm/purchaseOrder/fetchDetail';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import StockReceiptHeaderChargeFields from
  '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptHeaderChargeFields';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import StockReceiptLinesEditor from '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptLinesEditor';
import { STOCK_RECEIPT_RECEIPT_DATE_TIP } from '#/modules/imsMgmt/receipts/stockReceipt/helper/receiptDateTip';
import {
  resolveStockReceiptFieldLabel,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/formValidationLabels';
import {
  STOCK_RECEIPT_FIELD_LABELS,
} from '#/modules/imsMgmt/receipts/stockReceipt/schemas/stockReceiptSchema';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import {
  getPurchaseOrderApiErrorMessage,
} from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import {
  buildPurchaseOrderStockReceiptDefaultValues,
  buildPurchaseOrderStockReceiptPayload,
  purchaseOrderStockReceiptFormSchema,
} from '#/modules/srmMgmt/purchaseOrder/helper/mapPurchaseOrderToStockReceiptForm';
import {
  canCreateStockReceiptFromPurchaseOrder,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseOrderStatus';
import { toast } from '#/utils/toast';
import {
  lookupQueryKey,
  purchaseOrderDetailQueryKey,
  purchaseOrderStockReceiptListQueryKeyPrefix,
  stockReceiptListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreatePurchaseOrderStockReceiptPage = () => {
  const { id: purchaseOrderId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: purchaseOrderDetailQueryKey(purchaseOrderId),
    queryFn: () => fetchPurchaseOrderDetail(purchaseOrderId),
    enabled: Boolean(purchaseOrderId),
  });

  const warehouseOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const detail = detailQuery.data?.data;
  const defaultValues = useMemo(
    () => buildPurchaseOrderStockReceiptDefaultValues(detail),
    [detail],
  );
  const warehouseOptions = warehouseOptionsQuery.data ?? [];

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(purchaseOrderStockReceiptFormSchema),
    defaultValues: buildPurchaseOrderStockReceiptDefaultValues(null),
  });

  useEffect(() => {
    if (!detail) {
      return;
    }
    resetValidationSummary();
    reset(defaultValues);
  }, [detail, defaultValues, reset, resetValidationSummary]);

  const createMutation = useMutation({
    mutationFn: (values) => createPurchaseOrderStockReceipt(
      purchaseOrderId,
      buildPurchaseOrderStockReceiptPayload(values),
    ),
    onSuccess: async (result) => {
      toast.success(result?.message || '入庫單已建立');
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderDetailQueryKey(purchaseOrderId),
      });
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderStockReceiptListQueryKeyPrefix(purchaseOrderId),
      });
      await queryClient.invalidateQueries({
        queryKey: stockReceiptListQueryKeyPrefix(),
      });
      navigate(ROUTES.purchaseOrderDetail(purchaseOrderId));
    },
    onError: (error) => {
      toast.error(getPurchaseOrderApiErrorMessage(error, '建立入庫單失敗'));
    },
  });

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    if (!canCreateStockReceiptFromPurchaseOrder(detail?.status)) {
      toast.error('僅已核准或部分入庫的物料採購單可新建入庫');
      return;
    }
    createMutation.mutate(values);
  }

  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;
  const canCreate = canCreateStockReceiptFromPurchaseOrder(detail?.status);

  if (!isLoading && !isError && detail && !canCreate) {
    return <Navigate to={ROUTES.purchaseOrderDetail(purchaseOrderId)} replace />;
  }

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">
                物料採購單新建入庫
              </span>
              <DocumentStatusBadge status="DRAFT" size="sm" />
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.purchaseOrderDetail(purchaseOrderId)}>返回詳情</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getPurchaseOrderApiErrorMessage(detailQuery.error, '載入物料採購單失敗')}
            </p>
          ) : null}

          {!isLoading && !isError && detail ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                物料採購單
                {' '}
                {detail.purchaseOrderNo || purchaseOrderId}
                {' · '}
                供應商
                {' '}
                {detail.supplier?.name || detail.supplier?.code || '—'}
              </p>

              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={STOCK_RECEIPT_FIELD_LABELS}
                fieldLabelResolver={(path) => resolveStockReceiptFieldLabel(path, {
                  lines: getValues('lines'),
                })}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">單據資訊</h2>
                    </div>
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingDateInput
                        id="receiptDate"
                        label="製造商開單日"
                        tip={STOCK_RECEIPT_RECEIPT_DATE_TIP}
                        control={control}
                        errors={errors}
                        placeholder="西元年/月/日"
                      />
                      <FloatingDateInput
                        id="expectedArrivalDate"
                        label="預計到貨日"
                        control={control}
                        errors={errors}
                        placeholder="西元年/月/日"
                      />
                      <FloatingSelect
                        id="warehouseId"
                        label="倉別"
                        options={warehouseOptions}
                        control={control}
                        errors={errors}
                        isDisabled={warehouseOptionsQuery.isLoading}
                        noOptionsMessage="無可用倉別"
                      />
                      <FloatingTextarea
                        id="issueReason"
                        label="事由"
                        register={register}
                        errors={errors}
                        rows={2}
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                        rows={2}
                      />
                      <StockReceiptHeaderChargeFields
                        register={register}
                        errors={errors}
                      />
                    </div>
                  </section>

                  <StockReceiptLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                  />
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.purchaseOrderDetail(purchaseOrderId)}>取消</Link>
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || !canCreate}
                  >
                    {createMutation.isPending ? '建立中…' : '建立入庫單'}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreatePurchaseOrderStockReceiptPage;
